<script setup lang="ts">
import './global.interceptor.ts';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { type StoreGeneric, storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import Controls from './el/Controls.vue';
import SideBar from './el/SideBar.vue';
import Toast from './el/Toast.vue';
import { SlimOrganization, useMyOrganizations } from '@ilteducation/my-account';

import UserTable from './containers/UserTable.vue';
import GroupTable from './containers/GroupTable.vue';
import { useSideBar } from './composables/useSideBar';
import NewUserModal from './el/NewUser/NewUserModal.vue';
import NewGroupModal from './el/NewGroup/NewGroupModal.vue';
import UserSidePanel from './el/UserSidePanel/UserSidePanel.vue';
import { useSelectedItems } from './composables/useSelectedItems';
import UsersSidePanel from './el/UsersSidePanel/UsersSidePanel.vue';
import GroupSidePanel from './el/GroupSidePanel/GroupSidePanel.vue';
import GroupsSidePanel from './el/GroupsSidePanel/GroupsSidePanel.vue';
import CreateSingleUserModal from './el/NewUser/CreateSingleUserModal.vue';
import CreateMultipleUsersModal from './el/NewUser/CreateMultipleUsersModal.vue';

import {
  useDefaultGroupListRouteQuery,
  useDefaultListRouteQuery,
  useDefaultOrganizationRouteQuery,
  useDefaultUserListRouteQuery,
  useSelectedEntityRouteQuery,
} from './composables/default';
import { useCurrentOrganization } from './composables/useCurrentOrganization';
import { useEventStateManager } from './composables/useEventStateManager.js';
import useModal, { modals } from './composables/useModal';
import ImportUsersModal from './el/NewUser/ImportUsers/index.vue';
import { useEscalateLogin } from './composables/useEscalateLogin';
import { usePermissions } from './composables/usePermissions';
import { useUserContext } from '@ilteducation/ilt-auth';
import { SwitchValue } from './containers/UserGroupSwitch.vue';
/**
 * Event state manager.
 * requires to be initialized before everything.
 */
useEventStateManager();

type Props = {
  onEscalatedLoginRequested?: () => void;
};
const props = defineProps<Props>();

const router = useRouter();
const userContext = useUserContext();
const { isSuperUser, hasRole } = storeToRefs(userContext as unknown as StoreGeneric);

const { sideBarOpen, selectedEntity, closeSideBar } = useSideBar();
const { clearSelectedItems, setSelectedItems, selectedItems } = useSelectedItems();

const { open: openNewUserModal } = useModal(modals.NEW_USER);
const { open: openNewGroupModal } = useModal(modals.NEW_GROUP);
const { isOpen: isImportUsersOpen, open: openImportUsersModal } = useModal(modals.IMPORT_USERS);

const { param: userParam, setValue: setUserModelProperties } = useDefaultUserListRouteQuery();
const { param: groupParam, setValue: setGroupModelProperties } = useDefaultGroupListRouteQuery();
const { param: selectedList, setValue: setSelectedListProperties } = useDefaultListRouteQuery();
const { param: selectedOrganization, setValue: setOrganizationQueryParam } = useDefaultOrganizationRouteQuery();
const { param: selectedEntityUid, setValue: setSelectedEntity } = useSelectedEntityRouteQuery();

const setSelectedList = (value: string) => {
  clearSelectedItems();
  if (value === 'users') {
    setGroupModelProperties(undefined);
  } else {
    setUserModelProperties(undefined);
  }

  setSelectedEntity(undefined);
  setSelectedListProperties(value);
};

const onCloseSidePanelClick = () => {
  setSelectedItems([]);
  setSelectedEntity(undefined);
  clearSelectedItems();
  closeSideBar();
};

const { organizations } = useMyOrganizations(false);
const { setCurrentOrganization, organization } = useCurrentOrganization();

watch(
  [selectedOrganization, organizations],
  ([newOrganizationUid, newOrganizations]) => {
    const selectedOrg: SlimOrganization | undefined = newOrganizations.data.find(
      (org) => org.uid === newOrganizationUid,
    );
    setCurrentOrganization(selectedOrg ?? null);
  },
  { immediate: true },
);

const setSelectedOrganization = (organizationUid?: string) => {
  if (!organizationUid) {
    return;
  }
  setOrganizationQueryParam(organizationUid);

  setGroupModelProperties(undefined);
  setUserModelProperties(undefined);

  setSelectedEntity(undefined);
  setSelectedItems([]);
};

const setSearchString = (value?: string) => {
  clearSelectedItems();
  const newValue = value || undefined;
  if (selectedList.value === 'users') {
    setUserModelProperties({ ...userParam.value, searchString: newValue });
  } else {
    setGroupModelProperties({ ...userParam.value, searchString: newValue });
  }
};

const searchString = computed(() => {
  if (selectedList.value === 'users') {
    return userParam.value?.searchString;
  } else {
    return groupParam.value?.searchString;
  }
});

const isControlPanelLightEnabled = computed(() => {
  return isSuperUser.value || hasRole.value('admin') || hasRole.value('employee');
});

const userTable = ref();
const groupTable = ref();
watch(
  isControlPanelLightEnabled,
  async (enabled) => {
    if (!enabled && router.currentRoute.value.path.includes('/control-panel')) {
      // need to wait for the router to be ready before redirecting
      // otherwise it will throw an error :(
      await new Promise((resolve) => setTimeout(resolve, 1));
      // redirect to not found page now
      await router.isReady().then(() => {
        return router.push({ name: 'NotFound' });
      });
    }
  },
  { immediate: true },
);

const openImportUsers = () => {
  openImportUsersModal();
};

const { register, unregister } = useEscalateLogin();
const { revalidatePermissionCaches } = usePermissions();

const onEscalateLogin = () => {
  props.onEscalatedLoginRequested?.();
};

onMounted(() => {
  register(onEscalateLogin);
  revalidatePermissionCaches();
});

onUnmounted(() => {
  unregister(onEscalateLogin);
});

watch(
  selectedEntityUid,
  () => {
    if (selectedEntityUid.value) {
      setSelectedItems([selectedEntityUid.value]);
    }
  },
  { immediate: true },
);

watch(selectedItems, () => {
  if (selectedItems.value.length === 1) {
    selectedEntityUid.value = selectedItems.value[0];
  } else {
    selectedEntityUid.value = undefined;
  }
});

const selectedListAsSwitchValue = computed(() => {
  return selectedList.value as SwitchValue;
});
</script>
<template>
  <v-layout class="d-flex flex-column flex-1-0-100 h-100">
    <v-main class="control-panel-light d-flex flex-column flex-1-0-100 h-100">
      <v-container
        fluid
        :class="{ 'pa-0': true, 'pr-4': sideBarOpen }"
        class="d-flex flex-column flex-1-1-auto h-100 pt-4 pa-sm-6"
      >
        <v-row class="flex-grow-0 px-4 px-sm-0">
          <v-col cols="12">
            <Controls
              :list="selectedListAsSwitchValue"
              @setSelectedOrganization="setSelectedOrganization"
              @setSelectedList="setSelectedList"
              @setSearchString="setSearchString"
              :selectedOrganization="selectedOrganization"
              :searchString="searchString"
              :createAction="selectedList === 'users' ? openNewUserModal : openNewGroupModal"
              :importUsers="openImportUsers"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <UserTable
              ref="userTable"
              v-model="selectedItems"
              v-if="selectedList === 'users'"
              :organizationUid="organization.uid"
              class="h-100"
            />
            <GroupTable
              ref="groupTable"
              v-if="selectedList === 'groups'"
              v-model="selectedItems"
              :organizationUid="organization.uid"
              class="h-100"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-main>
    <SideBar :open="sideBarOpen && !!organization.uid">
      <template v-slot:content>
        <UserSidePanel
          v-if="selectedEntity === 'user' && selectedEntityUid && organization.uid !== ''"
          :user-uid="selectedEntityUid"
          :organization-uid="organization.uid"
          @close="onCloseSidePanelClick"
          @user-deleted="userTable?.reloadUsers"
          @user-updated="userTable?.reloadUsers"
        >
          <template #settings="slotContext">
            <slot name="settings.user" v-bind="slotContext"></slot>
          </template>
          <template #settings-modal="slotContext">
            <slot name="settings.user-modal" v-bind="slotContext"></slot>
          </template>
        </UserSidePanel>

        <UsersSidePanel
          v-if="selectedEntity === 'user-multiple' && organization.uid !== ''"
          :total-users="333"
          :organization-uid="organization.uid"
          @users-deleted="userTable?.reloadUsers"
          @close="onCloseSidePanelClick"
        >
          <template #settings="slotContext">
            <slot name="settings.users" v-bind="slotContext"></slot>
          </template>
        </UsersSidePanel>

        <GroupSidePanel
          v-if="selectedEntity === 'group' && selectedEntityUid && organization.uid !== ''"
          :group-uid="selectedEntityUid"
          :school-uid="organization.uid"
          @group-deleted="groupTable?.reloadGroups"
          @group-updated="groupTable?.reloadGroups"
          @close="onCloseSidePanelClick"
        >
          <template #settings="slotContext">
            <slot name="settings.group" v-bind="slotContext"></slot>
          </template>
          <template #settings-modal="slotContext">
            <slot name="settings.group-modal" v-bind="slotContext"></slot>
          </template>
        </GroupSidePanel>
        <GroupsSidePanel
          v-if="selectedEntity === 'group-multiple' && organization.uid !== ''"
          @groups-deleted="groupTable?.reloadGroups"
          @close="onCloseSidePanelClick"
        >
          <template #settings="slotContext">
            <slot name="settings.groups" v-bind="slotContext"></slot>
          </template>
        </GroupsSidePanel>
      </template>

      <!-- TODO use it in Groups sidePanel -->
      <slot
        v-if="selectedEntity === 'group-multiple' && selectedEntityUid"
        name="settings.groups"
        v-bind="{ selectedGroups: [] }"
      ></slot>
    </SideBar>
    <NewUserModal v-if="organization.uid" :name="modals.NEW_USER" :school-uid="organization.uid" />
    <NewGroupModal v-if="organization.uid" :name="modals.NEW_GROUP" />
    <CreateSingleUserModal
      v-if="organization.uid"
      :name="modals.CREATE_SINGLE_USER"
      :organization="organization"
      @create-more-users-click="openNewUserModal"
    />
    <CreateMultipleUsersModal v-if="organization.uid" :name="modals.CREATE_MULTIPLE_USERS" />
    <ImportUsersModal
      v-if="isImportUsersOpen && organization.uid"
      :organization-uid="organization.uid"
      :name="modals.IMPORT_USERS"
    />
  </v-layout>
  <Toast />
</template>

<style scoped lang="scss"></style>
